import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MuiCard from '@mui/material/Card';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import SitemarkIcon from '../shared/components/SitemarkIcon';
import { signInWithEmailAndPassword, signInWithCustomToken, setPersistence, browserSessionPersistence, getMultiFactorResolver } from 'firebase/auth';
import { signInWithTrustedDevice, trustDevice } from '../services/adminService';
import { deriveDeviceLabel, PENDING_TRUST_DEVICE_KEY } from '../mfa/trustedDeviceUtils';
import { getUserDetails } from '../services/userService';
import { resetOfflineDemoState, syncOfflineServiceWorker } from '../services/offlineService';
import { useNavigate } from 'react-router-dom';
import MfaChallengeCard from '../mfa/MfaChallengeCard';
import { useAuth } from '../shared/context/AuthContext';

const AUTH_ERROR_MFA_REQUIRED = 'auth/multi-factor-auth-required';
const AUTH_ERROR_INVALID_CREDENTIAL = 'auth/invalid-credential';

/** @param {unknown} err */
function isFirestoreOfflineError(err) {
  return err?.code === 'unavailable' || /client is offline/i.test(err?.message || '');
}

function waitForBrowserOnline(timeoutMs = 4000) {
  if (typeof window === 'undefined' || typeof navigator === 'undefined' || navigator.onLine) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    let resolved = false;
    const timeoutId = window.setTimeout(() => {
      if (resolved) return;
      resolved = true;
      window.removeEventListener('online', handleOnline);
      resolve(navigator.onLine);
    }, timeoutMs);

    const handleOnline = () => {
      if (resolved) return;
      resolved = true;
      window.clearTimeout(timeoutId);
      window.removeEventListener('online', handleOnline);
      resolve(true);
    };

    window.addEventListener('online', handleOnline);
  });
}

/** @param {string} uid */
async function getUserDetailsWithReconnect(uid) {
  try {
    return await getUserDetails(uid);
  } catch (err) {
    if (!isFirestoreOfflineError(err)) throw err;

    const isOnline = await waitForBrowserOnline();
    if (!isOnline) throw err;

    return getUserDetails(uid);
  }
}

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

export default function SignInCard() {
  const invalidCredsRef = useRef(/** @type {HTMLElement | null} */ (null));
  const emailRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const passwordRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [mfaResolver, setMfaResolver] = useState(/** @type {any} */ (null));
  const [isSigningIn, setIsSigningIn] = useState(false);

  const { auth, user, claims, isAuthResolved } = useAuth();
  const navigate = useNavigate()

  useEffect(() => {
    if (!isAuthResolved || !user) return;

    let isMounted = true;

    (async () => {
      // Consume the trust-device intent written synchronously by MfaChallengeCard
      // before resolveSignIn. Calling trustDevice here (before navigate) ensures the
      // result is known before SessionTimeoutGuard mounts.
      let evictionNotice = /** @type {{ label: string; count: number; maxDevices: number } | null} */ (null);
      const shouldTrust = (() => {
        try {
          const val = sessionStorage.getItem(PENDING_TRUST_DEVICE_KEY);
          if (val) { sessionStorage.removeItem(PENDING_TRUST_DEVICE_KEY); return true; }
          return false;
        } catch { return false; }
      })();

      // Fire both independently - neither depends on the other's result.
      const trustPromise = shouldTrust
        ? trustDevice(deriveDeviceLabel()).catch(() => null)
        : Promise.resolve(null);

      try {
        const [trustResult, userInfo] = await Promise.all([
          trustPromise,
          /** @type {Promise<{patientId?: string} | null>} */ (getUserDetailsWithReconnect(user.uid)),
        ]);

        if (trustResult?.evictedCount > 0) {
          evictionNotice = { label: trustResult.evictedDeviceLabel, count: trustResult.evictedCount, maxDevices: trustResult.maxDevices };
        }

        const navigationState = evictionNotice ? { evictionNotice } : undefined;
        const isDemo = !!claims?.isDemo;
        const isDemoOffline = isDemo && !!claims?.isOffline;
        const patientId = userInfo?.patientId;

        if (isDemoOffline) {
          await syncOfflineServiceWorker(true);
        } else {
          void resetOfflineDemoState().catch((cleanupErr) => {
            console.warn('regular-user offline cleanup failed', cleanupErr);
          });
        }

        if (!patientId) {
          if (isMounted) navigate('/patients', { replace: true, state: navigationState });
          return;
        }

        if (isMounted) navigate('/dashboard', { replace: true, state: navigationState });
      } catch (err) {
        if (isFirestoreOfflineError(err)) {
          console.warn('login redirect delayed while reconnecting to Firestore', err);
          if (isMounted) navigate('/patients', { replace: true });
          return;
        }
        console.error('login redirect check failed', err);
        if (isMounted) setIsSigningIn(false);
      }
    })();

    return () => { isMounted = false; };
  }, [isAuthResolved, user, claims, navigate]);

  /** @param {React.FormEvent<HTMLFormElement>} event */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateInputs()) return;

    const data = new FormData(event.currentTarget);
    const emailValue = data.get('email');
    const passwordValue = data.get('password');

    if (typeof emailValue !== 'string' || typeof passwordValue !== 'string') return;

    setIsSigningIn(true);

    try {
      await setPersistence(auth, browserSessionPersistence);
      await signInWithEmailAndPassword(auth, emailValue, passwordValue);
      if (invalidCredsRef.current) invalidCredsRef.current.hidden = true;
    } catch (error) {
      const code = /** @type {any} */ (error)?.code;
      if (code === AUTH_ERROR_MFA_REQUIRED) {
        const resolver = getMultiFactorResolver(auth, /** @type {any} */ (error));

        try {
          const trustedDeviceResult = await signInWithTrustedDevice(emailValue, passwordValue);
          if (trustedDeviceResult?.customToken) {
            await signInWithCustomToken(auth, trustedDeviceResult.customToken);
            return;
          }
        } catch {
          // Invalid credentials or trusted-device lookup failure - fall through to TOTP challenge
        }

        setIsSigningIn(false);
        setMfaResolver(resolver);
      } else if (code === AUTH_ERROR_INVALID_CREDENTIAL) {
        setIsSigningIn(false);
        if (invalidCredsRef.current) invalidCredsRef.current.hidden = false;
      } else {
        setIsSigningIn(false);
      }
    }
  };

  const validateInputs = () => {
    const email = emailRef.current;
    const password = passwordRef.current;

    let isValid = true;

    if (!email || !password) return false;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  if (mfaResolver) {
    return <MfaChallengeCard resolver={mfaResolver} onBack={() => setMfaResolver(null)} />;
  }

  return (
    <Card variant="outlined">
      <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
        <SitemarkIcon sx={{ height: { xs: 28, sm: 36, md: 40 }, width: { xs: 120, sm: 160, md: 200 }, mr: 2, color: '#000000' }} />
      </Box>
      <Typography
        component="h1"
        variant="h4"
        sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
      >
        Sign in
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit}
        action="javascript:void(0)"
        method="post"
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
      >
        <FormControl>
          <FormLabel htmlFor="email">Email</FormLabel>
          <TextField
            error={emailError}
            helperText={emailErrorMessage}
            id="email"
            type="email"
            name="email"
            placeholder="your@email.com"
            autoComplete="email"
            autoFocus
            required
            fullWidth
            variant="outlined"
            color={emailError ? 'error' : 'primary'}
            inputProps={{ 'aria-label': 'email' }}
            inputRef={emailRef}
          />
        </FormControl>
        <FormControl>
          <FormLabel htmlFor="password">Password</FormLabel>
          <TextField
            error={passwordError}
            helperText={passwordErrorMessage}
            name="password"
            placeholder="••••••"
            type="password"
            id="password"
            autoComplete="current-password"
            required
            fullWidth
            variant="outlined"
            color={passwordError ? 'error' : 'primary'}
            inputRef={passwordRef}
          />
        </FormControl>
        <Button
          sx={{ my: 5 }}
          type="submit"
          fullWidth
          variant="contained"
          disabled={isSigningIn}
          aria-label={isSigningIn ? 'Signing in' : undefined}
        >
          {isSigningIn ? <CircularProgress size={24} color="inherit" /> : 'Sign in'}
        </Button>
        <Typography ref={invalidCredsRef} color='error' hidden={true}>Invalid credentials</Typography>
      </Box>
    </Card>
  );
}
